import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import React, { lazy, Suspense, useEffect } from "react";
import Navigation from "./components/Navigation";
import ScrollToTop from "./components/ScrollToTop";
import { preloadAllPages } from "./utils/pagePreloader";
import "./App.css";

// Preload functions for instant navigation
const preloadHome = () => import("./pages/Home");
const preloadFinancial = () => import("./pages/Financial");
const preloadBrewers = () => import("./pages/Brewers");
const preloadEats = () => import("./pages/Eats");
const preloadVacancies = () => import("./pages/Vacancies");
const preloadStore = () => import("./pages/Store");

// Lazy load all pages for code splitting
const Home = lazy(preloadHome);
const Financial = lazy(preloadFinancial);
const Brewers = lazy(preloadBrewers);
const Eats = lazy(preloadEats);
const Vacancies = lazy(preloadVacancies);
const Store = lazy(preloadStore);
const SellerRegister = lazy(() => import("./pages/SellerRegister"));
const SellerLogin = lazy(() => import("./pages/SellerLogin"));
const SellerPending = lazy(() => import("./pages/SellerPending"));
const SellerDashboard = lazy(() => import("./pages/SellerDashboard"));
const SellerSubmitItem = lazy(() => import("./pages/SellerSubmitItem"));
const SellerVerifyOTP = lazy(() => import("./pages/SellerVerifyOTP"));
const SellerForgotPassword = lazy(() => import("./pages/SellerForgotPassword"));
const SellerResetPassword = lazy(() => import("./pages/SellerResetPassword"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Export preload functions for Navigation component
export {
  preloadHome,
  preloadFinancial,
  preloadBrewers,
  preloadEats,
  preloadVacancies,
  preloadStore,
};

// Minimal loading component - only shows after 200ms delay
const PageLoader = () => {
  const [show, setShow] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShow(true), 200);
    return () => clearTimeout(timer);
  }, []);

  if (!show) return null;

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "50vh",
        padding: "40px",
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          border: "3px solid rgba(255, 102, 0, 0.2)",
          borderTop: "3px solid #fbbf24",
          borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }}
      ></div>
    </div>
  );
};

function App() {
  // Preload all pages and images on mount
  useEffect(() => {
    preloadAllPages();
  }, []);

  return (
    <Router>
      <div className="App">
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route
              path="*"
              element={
                <>
                  <Navigation />
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/financial" element={<Financial />} />
                      <Route path="/brewers" element={<Brewers />} />
                      <Route path="/eats" element={<Eats />} />
                      <Route path="/vacancies" element={<Vacancies />} />
                      <Route path="/store" element={<Store />} />
                      <Route
                        path="/seller/register"
                        element={<SellerRegister />}
                      />
                      <Route path="/seller/login" element={<SellerLogin />} />
                      <Route
                        path="/seller/verify-otp"
                        element={<SellerVerifyOTP />}
                      />
                      <Route
                        path="/seller/forgot-password"
                        element={<SellerForgotPassword />}
                      />
                      <Route
                        path="/seller/reset-password"
                        element={<SellerResetPassword />}
                      />
                      <Route
                        path="/seller/pending"
                        element={<SellerPending />}
                      />
                      <Route
                        path="/seller/dashboard"
                        element={<SellerDashboard />}
                      />
                      <Route
                        path="/seller/submit-item"
                        element={<SellerSubmitItem />}
                      />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Suspense>
                  <ScrollToTop />
                </>
              }
            />
          </Routes>
        </Suspense>
      </div>
    </Router>
  );
}

export default App;
